<template>
  <div class="card-doctor-details">
    <div class="card-doctor-details__info">
      <smd-profile-img :image="doctorImageURL"
                       :size="$isDesktop ? 'l' : 'm'"/>

      <div class="doctor-details">
        <p class="Title3">{{ doctorName }}</p>
        <p class="Body1">{{ title }}</p>
      </div>
    </div>

    <div v-if="!availableNow" class="card-doctor-details__more-info">
      <div class="availability">
        <p :class="$isDesktop ? 'Body5' : 'Label2'">Consultation Availability</p>
        <div>
          <div class="flex-row--center-x">
            <span :class="[
              availableForOnline ? 'smd-icon-util-check' : 'smd-icon-action-close',
            ]">
            </span>
            <p class="Body1">Online</p>
          </div>
          <div class="flex-row--center-x">
            <span :class="[
              availableInPerson ? 'smd-icon-util-check' : 'smd-icon-action-close',
            ]">
            </span>
            <p class="Body1">In-Person</p>
          </div>
        </div>
      </div>

      <div v-if="experience" class="experience">
        <p :class="$isDesktop ? 'Body5' : 'Label2'">Experience</p>
        <p class="Body1">{{ experience }} {{ experience === 1 ? 'year' : 'years' }}</p>
      </div>
    </div>

    <div v-if="!availableNow" class="card-doctor-details__eas">
      <p :class="$isDesktop ? 'Body5' : 'Label2'">Earliest Available Schedule</p>
      <div>
        <div class="icon-container">
          <img :src="clinicImage" alt="Clinic Image"/>
        </div>
        <div class="text-container">
          <div :class="$isDesktop ? 'Body2' : 'Body6'">{{ clinicName }}</div>
          <p class="Paragraph1">{{ earliestAvailableScheduleFormat }}</p>
          <p v-if="earliestAvailableScheduleFee" class="Paragraph1">Fee: 
            <span>{{ earliestAvailableScheduleFee[0] | currency }}</span>
            <span v-if="earliestAvailableScheduleFee[1]"> - {{ earliestAvailableScheduleFee[1] | currency }}</span>
          </p>
          <p v-if="!earliestAvailableScheduleFee" class="Paragraph1">Fee: --</p>
        </div>
      </div>
    </div>

    <div v-if="availableNow" class="card-doctor-details__buttons-container">
      <smd-btn type="button"
               section="5"
               variant="left-enabled"
               @click="viewDoctorProfile">
        View Profile
      </smd-btn>
      <smd-btn type="button"
               section="5"
               variant="right-green-enabled"
               @click="bookDoctor">
        Consult Now
        <p v-if="campaignFee">{{ campaignFee | currency }}</p>
        <p v-else>--</p>
      </smd-btn>
    </div>

    <div v-if="!availableNow" class="card-doctor-details__buttons-container">
      <smd-btn type="button"
               section="5"
               variant="left-enabled"
               @click="bookDoctor">
        Book Doctor
      </smd-btn>
      <smd-btn type="button"
               section="5"
               variant="right-blue-enabled"
               @click="viewDoctorProfile">
        View Profile
      </smd-btn>
    </div>
  </div>
</template>

<script>
  import moment from 'moment';
  import {
    findEarliestAvailableSchedule,
    getAggregatedBlockoffSchedule,
    getClinicFeeRange,
    formatClinicTimeSchedule,
  } from '@leapfroggr/patientappcommons';
  import {getStaticImage} from '@/utils';
  import AvatarMale from '@/assets/images/avatars/doc-avatar-male.png';
  import AvatarFemale from '@/assets/images/avatars/doc-avatar-female.png';

  export default {
    name: 'card-doctor-details',
    props: {
      doctor: {type: Object, required: true},
      campaignId: {type: String, required: true},
    },
    computed: {
      doctorImageURL() {
        if (this.doctor.image) {
          return getStaticImage(this.doctor.image);
        }

        return this.doctor.gender === '0' ? AvatarMale : AvatarFemale;
      },
      doctorName() {
        let tempStr = '';

        if (!this.doctor.provider && !this.doctor.hideSalutation) {
          tempStr = 'Dr. ';
        }

        return `${tempStr}${this.doctor.firstname} ${this.doctor.lastname} ${this.doctor.suffix || ''}`.trim();
      },
      title() {
        return this.doctor.title;
      },
      availableNow() {
        return !!this.doctor.onDemandSession;
      },
      onDemandCampaign() {
        if (!this.doctor.onDemandSession) return null;

        return this.doctor.onDemandSession.campaigns.find(({campaignid}) => this.campaignId === campaignid);
      },
      campaignFee() {
        if (this.availableNow && this.onDemandCampaign) {
          const defaultFee = this.onDemandCampaign.services[0].fees.find(fee => {
            return fee.feetype === 1;
          });

          if (defaultFee) {
            const total = defaultFee.service_charge + defaultFee.subtotal;
            return total;
          }

          return null;
        }

        return null;
      },
      experience() {
        return (this.doctor.professional && this.doctor.professional.practicingSince)
         ? (new Date()).getFullYear() - this.doctor.professional.practicingSince
         : 0;
      },
      earliestAvailableSchedule() {
        return findEarliestAvailableSchedule(
          this.doctor.clinics,
          getAggregatedBlockoffSchedule(this.doctor.blockOffs),
          this.doctor.LatestBookingTimeNoSlotReference,
          (new Date()).toISOString(),
        );
      },
      clinicName() {
        return this.earliestAvailableSchedule
          ? this.earliestAvailableSchedule.clinic.name
          : '--';
      },
      availableForOnline() {
        return this.earliestAvailableSchedule
          ? this.earliestAvailableSchedule.clinic.virtual
          : false;
      },
      availableInPerson() {
        return this.earliestAvailableSchedule
          ? this.earliestAvailableSchedule.clinic.onsite
          : false;
      },
      clinicImage() {
        const defaultImage = 'https://seriousmd.com/Images/clinic-avatar.png';

        if (!this.earliestAvailableSchedule) {
          return defaultImage;
        }

        if (this.earliestAvailableSchedule.clinic.name === 'Online Clinic') {
          return 'https://static.seriousmd.com/assets/online-clinic.svg';
        }

        if (this.earliestAvailableSchedule.clinic.clinicimage) {
          return getStaticImage(this.earliestAvailableSchedule.clinic.clinicimage);
        }

        return defaultImage;
      },
      earliestAvailableScheduleFormat() {
        if (!this.earliestAvailableSchedule) {
          return '--';
        }

        let date = moment().add(this.earliestAvailableSchedule.gap, 'days').format('MMM D, YYYY');

        if (this.earliestAvailableSchedule.gap === 0) {
          date = 'Today';
        }

        if (this.earliestAvailableSchedule.gap === 1) {
          date = 'Tomorrow';
        }

        const start = formatClinicTimeSchedule(this.earliestAvailableSchedule.schedule.starttime, 'h:mm A');
        const end = formatClinicTimeSchedule(this.earliestAvailableSchedule.schedule.endtime, 'h:mm A');

        return `${date}, ${start} - ${end}`;
      },
      earliestAvailableScheduleFee() {
        if (!this.earliestAvailableSchedule) return null;

        const { lowest, highest } = getClinicFeeRange(this.earliestAvailableSchedule.clinic);

        if (lowest === 0 && highest === 0) {
          return null;
        }

        if (lowest === highest) {
          return [highest];
        }

        return [lowest, highest];
      },
    },
    methods: {
      viewDoctorProfile() {
        window.location.href = `${process.env.VUE_APP_DOCTOR_PROFILE_AND_BOOKING_URL}/doctorprofilepage/${this.doctor.usertag}`;
      },
      bookDoctor() {
        let queryParamString = '';

        if (this.earliestAvailableSchedule) {
          const virtual = this.earliestAvailableSchedule.clinic.virtual || this.earliestAvailableSchedule.clinic.onsite;
          const clinicid = this.earliestAvailableSchedule.clinic.clinicid;
          const appointmentdate = moment().add(this.earliestAvailableSchedule.gap, 'days').toISOString();

          queryParamString = `?onDemand=${this.availableNow}&virtual=${virtual}&clinicid=${clinicid}&appointmentdate=${appointmentdate}`;
        }
        
        window.location.href = `${process.env.VUE_APP_DOCTOR_PROFILE_AND_BOOKING_URL}/doctorbooking/${this.doctor.usertag}${queryParamString}`;
      },
    },
  }
</script>

<style lang="scss">
  @import "@/assets/styles/applications.scss";

  .card-doctor-details {
    @include box-shadow();
    @extend %layout--flex-col;
    gap: 16px;
    background-color: color(white);
    border-radius: 10px;
    width: 98%;

    @include responsive(tablet) {
      gap: 12px;
    }

    &__info,
    &__more-info,
    &__eas {
      padding-left: 30px;
      padding-right: 30px;

      @include responsive(tablet) {
        padding-left: 20px;
        padding-right: 20px;
      }
    }

    &__info {
      @extend %layout--flex-row-center-x;
      gap: 8px;
      padding-top: 30px;

      @include responsive(tablet) {
        padding-top: 15px;
      }
    }

    &__more-info {
      @extend %layout--flex-row-baseline-first;
      gap: 36px;

      @include responsive('max-width: 375px') {
        gap: 16px;
      }

      .availability,
      .experience {
        @extend %layout--flex-col;
        gap: 4px;
      }

      .availability {
        > :nth-child(2) {
          @extend %layout--flex-row;
          gap: 8px;

          > * {
            gap: 4px;
          }

          [class^=smd-icon-] {
            color: color(gray-200);
          }

          [class^=smd-icon-util-check] {
            color: color(mountainGreen);
          }
        }
      }
    }

    &__eas {
      @extend %layout--flex-col;
      gap: 8px;

      > :nth-child(2) {
        @extend %layout--flex-row;
        gap: 15px;
      }

      .icon-container {
        min-width: 75px;
        min-height: 75px;
        max-width: 75px;
        max-height: 75px;
        border: 1px solid color(gray-100);
        padding: 4px;
        border-radius: 6px;

        @include responsive(mobile) {
          min-width: 60px;
          min-height: 60px;
          max-width: 60px;
          max-height: 60px;
          padding: 6px;
        }

        img {
          width: 100%;
          height: 100%;
          display: block;
        }
      }
    }

    &__buttons-container {
      .consult-now-btn {
        span {
          display: block;
        }
      }
    }
  }
</style>
